import React from 'react';
import { Box } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { useMutation } from 'react-query';
import axios, { endPoints } from 'api/request';
import throttle from 'lodash/throttle';
import { useRecoilState, useRecoilValue } from 'recoil';
import SearchIcon from '@mui/icons-material/Search';
import { OrderPayload, isValidOwners, urlParams } from 'pages/reference-implementation/provisioning/RI003AWS/store';
import { CssTextField } from 'pages/reference-implementation/components/select';
import { InputLabel } from 'pages/reference-implementation/components/input-label';
import { RIStyles } from 'pages/reference-implementation/Style';
import { RICatalogCode003, RICatalogCode003O, RICatalogCode003A, RICatalogCode005 } from 'pages/reference-implementation/utils/RICatalogCode';
import { isUniqueOwner } from 'pages/reference-implementation/provisioning/RI003AWS/utils';

const AppOwnerSearch = () => {
    const [isTouched, setIsTouched] = React.useState(false);
    const [order, setOrder] = useRecoilState(OrderPayload);
    const [value, setValue] = React.useState(null);
    const [ccInputValue, setCCInputValue] = React.useState('-');
    const [searched, setSearched] = React.useState(false);
    const [options, setOptions] = React.useState([]);
    const [validOwner, setValidOwner] = useRecoilState(isValidOwners);
    const paramValue = useRecoilValue(urlParams);

    const appOwnerApi = useMutation(() => axios.get(`${endPoints.riServices.COMMON.appOwner}?key=${ccInputValue}`));
    const appOwnerPostApi = useMutation((payload) => axios.get(`${endPoints.riServices.COMMON.appOwner}?key=${payload}`));

    const setAppOwner = (appOwner, appOwnerDetails) => {
        setOrder({
            ...order,
            appOwner,
            appOwnerDetails,
        });
    };
    React.useEffect(() => {
        if (!order?.appOwner && !order?.appOwnerDetails) {
            setOptions([]);
        }
    }, [order?.appOwner, order?.appOwnerDetails]);

    const resetOptions = () => {
        setOptions([]);
        setAppOwner(null, null);
    };
    const fetch = React.useMemo(
        () =>
            throttle(() => {
                appOwnerApi.mutate();
            }, 1000),
        []
    );
    const handleSearch = () => {
        // istanbul ignore if
        if (ccInputValue === '') {
            resetOptions();
        }
        // istanbul ignore else
        if (ccInputValue !== '' && ccInputValue.length > 2) {
            setSearched(true);
            fetch();
        }
    };
    const isExistsInOptions = (appownerValue) => options.find((option) => appownerValue === option.name);

    React.useEffect(() => {
        if (options && options?.length === 1 && !order?.appOwner && order?.appOwnerDetails) {
            setOrder({ ...order, appOwner: options[0]?.label, appOwnerDetails: options?.[0] });
        }
        if (options && options?.length > 1 && order?.appOwnerDetails && !order?.appOwner) {
            const appOwerObj = isExistsInOptions(order?.appOwnerDetails?.name);
            setOrder({ ...order, appOwner: options[0]?.label, appOwnerDetails: appOwerObj });
        }
    }, [options]);
    React.useEffect(() => {
        if (appOwnerApi?.data?.data) {
            const combinedValue = appOwnerApi?.data?.data?.map((app) => ({
                label: `${app.gpid} - ${app.name}`,
                gpid: `${app.gpid}`,
                name: `${app.name}`,
                email: `${app.email}`,
            }));
            setOptions(combinedValue);
        }
    }, [appOwnerApi?.data?.data]);

    React.useEffect(() => {
        if (appOwnerPostApi?.data?.data) {
            const combinedValue = appOwnerPostApi?.data?.data?.map((app) => ({
                label: `${app.gpid} - ${app.name}`,
                gpid: `${app.gpid}`,
                name: `${app.name}`,
                email: `${app.email}`,
            }));
            setOptions(combinedValue);
        }
    }, [appOwnerPostApi?.data?.data]);

    React.useEffect(() => {
        if (value === null) {
            // resetOptions();
        } else {
            setOptions(options?.filter((z) => z?.gpid === value?.gpid));
            setAppOwner(value?.label, value);
        }
    }, [value]);

    React.useEffect(() => {
        if (!order?.appOwner && order?.appOwnerDetails && order?.appShortName) {
            // appOwnerPostApi.mutate(order?.appOwnerDetails?.name);
            appOwnerPostApi.mutate(order?.appOwnerDetails?.gpid);
        }
    }, [order?.appOwner, order?.appOwnerDetails, order?.appShortName]);

    React.useEffect(() => {
        if (!order?.appOwner && order?.isTouched) setIsTouched(true);
        else setIsTouched(false);
    }, [order?.appOwner, order?.isTouched]);

    const validateInput = () => {
        if ((isTouched && order?.isTouched && !order?.appOwner) || isTouched || validOwner?.isvalidAppOwner === false) {
            return 'validate';
        }
        return '';
    };
    const getNoOptionText = () => {
        let text = '';
        if (searched === false) {
            text = 'Type & press enter to search by GPID, Firstname and Lastname';
        } else if (options?.length === 0) {
            text = 'No results found';
        }
        return text;
    };
    React.useEffect(() => {
        if (order?.appOwner && [RICatalogCode003, RICatalogCode003O, RICatalogCode003A, RICatalogCode005]?.includes(order?.catalogCode)) {
            const checkValidOwner = isUniqueOwner(order?.appOwner, [order?.certificateOwnerOne, order?.certificateOwnerTwo]);
            if (!checkValidOwner) {
                setValidOwner({ ...validOwner, isvalidAppOwner: false });
            } else {
                setValidOwner({ ...validOwner, isvalidAppOwner: true });
            }
        }
    }, [order?.appOwner]);
    return (
        <>
            <Box display="flex" alignItems="center" gap={1}>
                <Autocomplete
                    data-testid="app-owner-search"
                    key="appOwnerDetails"
                    size="small"
                    noOptionsText={getNoOptionText()}
                    loading={appOwnerApi?.isLoading}
                    value={order?.appOwner || null}
                    onInputChange={(event, newInputValue) => {
                        if (newInputValue) setCCInputValue(newInputValue?.includes('-') ? newInputValue?.split('-')?.pop()?.trim() : newInputValue);
                    }}
                    className={validateInput()}
                    onChange={(event, newValue) => {
                        // istanbul ignore else
                        if (typeof newValue === 'string') {
                            setValue({
                                label: newValue,
                            });
                        } else if (newValue && newValue.inputValue) {
                            // Create a new value from the user input
                            setValue({
                                label: newValue.inputValue,
                            });
                        } else {
                            setValue(newValue);
                        }
                    }}
                    //    filterOptions={filterOptions}

                    filterOptions={(optionValues, params) => {
                        // eslint-disable-next-line no-underscore-dangle
                        const _results = [];
                        // eslint-disable-next-line no-plusplus
                        for (let i = 0; i < optionValues.length; i++) {
                            // eslint-disable-next-line no-restricted-syntax
                            for (const key in optionValues[i]) {
                                if (optionValues[i][key] !== null && typeof optionValues[i][key] !== 'boolean' && typeof optionValues[i][key] !== 'number') {
                                    if (optionValues[i][key].toLowerCase().includes(params?.inputValue.toLowerCase())) {
                                        _results.push(optionValues[i]);
                                    }
                                }
                            }
                        }
                        return [...new Set(_results)];
                        // return _results
                    }}
                    selectOnFocus
                    clearOnBlur
                    handleHomeEndKeys
                    disableClearable
                    id="free-solo-with-text-demo"
                    options={options}
                    isOptionEqualToValue={(option, value1) => option?.label === value1}
                    sx={{ width: '100%' }}
                    onBlur={() => {
                        if (!value || !order?.appOwner) {
                            setSearched(false);
                            setOptions([]);
                            setValidOwner({ ...validOwner, isvalidAppOwner: true });
                        }
                    }}
                    onKeyUp={(event) => {
                        if (event.key === 'Enter') {
                            setSearched(true);
                            handleSearch();
                        }
                        if ((event.key === 'Backspace' || event.key === 'Delete') && (!event.target.value || event.target.value === '')) {
                            setSearched(false);
                            resetOptions();
                            setValidOwner({ ...validOwner, isvalidAppOwner: true });
                        }
                    }}
                    renderInput={(params) => (
                        <CssTextField
                            size="large"
                            // eslint-disable-next-line react/jsx-props-no-spreading
                            {...params}
                            label={<InputLabel label="Application Owner" isRequired />}
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        <SearchIcon
                                            sx={RIStyles?.form?.searchIcon}
                                            onClick={() => {
                                                setSearched(true);
                                                handleSearch();
                                            }}
                                            data-testid="search-btn"
                                        />
                                    </>
                                ),
                            }}
                            disabled={paramValue?.journeyId}
                            fullWidth
                            error={validOwner?.isvalidAppOwner === false}
                            helperText={validOwner?.isvalidAppOwner === false && <span>Please Choose different member</span>}
                            className={validateInput()}
                        />
                    )}
                />
            </Box>
        </>
    );
};

export default React.memo(AppOwnerSearch);
